import React, { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Chip from "@mui/material/Chip";
import Box from "@mui/material/Box";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import OutlinedInput from "@mui/material/OutlinedInput";
import CircularProgress from "@mui/material/CircularProgress";
import { useHistory, useParams } from "react-router-dom";
import UploadBox from "../../../../shared/molecules/fields/UploadBox";
import autoGradeClient from "../../../../utils/graphql/autoGradeClient";
import { GET_PRESIGNED_URL } from "../../../../utils/graphql/services/ai-evaluation/queries";
import { useMutation, useQuery } from "@apollo/client";
import { UPDATE_EXAM_PAPER } from "../../../../utils/graphql/services/ai-evaluation/mutations";
import { GET_ALL_EXAM_PAPERS } from "../../../../utils/graphql/services/ai-evaluation/queries";
import { Alert, Snackbar } from "@mui/material";

type CreateExamInput = {
  title: string;
  questionPaperPublicPdfUrl: string;
  scheduledDate: string;
  totalSections: number;
  totalMarks: number;
  totalQuestions: number;
  questionTypes: string[];
  bloomsTaxonomyOne: string[];
  bloomsTaxonomyTwo: string[];
  isAutogradeEnabled: boolean;
  isAutomarksEnabled: boolean;
};

const DEFAULT_INPUT: CreateExamInput = {
  title: "",
  questionPaperPublicPdfUrl: "",
  scheduledDate: "",
  totalSections: 0,
  totalMarks: 0,
  totalQuestions: 0,
  questionTypes: [],
  bloomsTaxonomyOne: [],
  bloomsTaxonomyTwo: [],
  isAutogradeEnabled: true,
  isAutomarksEnabled: false,
};

const QUESTION_TYPE_OPTIONS = [
  "MCQ",
  "Descriptive",
  "True/False",
  "Fill in the blanks",
];

const BLOOMS_TAXONOMY1 = [
  "Remember",
  "Understand",
  "Apply",
  "Analyze",
  "Evaluate",
  "Create",
];
const BLOOMS_TAXONOMY2 = [
  "Factual",
  "Conceptual",
  "Procedural",
  "Metacognitive",
];

function UpdateExamPaperPage() {
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const [form, setForm] = useState<CreateExamInput>(DEFAULT_INPUT);
  const [originalData, setOriginalData] =
    useState<CreateExamInput>(DEFAULT_INPUT);
  const [uploadedFileName, setUploadedFileName] = useState<string>("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">(
    "success"
  );

  const { data, loading, error } = useQuery(GET_ALL_EXAM_PAPERS, {
    client: autoGradeClient,
    variables: { input: { id: Number(id) } },
    skip: !id,
  });

  const [updateExamPaper, { loading: updating }] = useMutation(
    UPDATE_EXAM_PAPER,
    {
      client: autoGradeClient,
    }
  );

  useEffect(() => {
    if (data?.getExamPapers?.examPapers?.[0]) {
      const examPaper = data.getExamPapers.examPapers[0];

      let formattedDate = examPaper.scheduledDate;
      if (formattedDate) {
        formattedDate = new Date(formattedDate).toISOString().slice(0, 16);
      }

      const displayQuestionTypes = (examPaper.questionTypes || []).map(
        (qt: string) => QUESTION_TYPE_OPTIONS[qt] || qt
      );

      const formData = {
        title: examPaper.title || "",
        questionPaperPublicPdfUrl: examPaper.questionPaperPublicPdfUrl || "",
        scheduledDate: formattedDate || "",
        totalSections: examPaper.totalSections || 0,
        totalMarks: examPaper.totalMarks || 0,
        totalQuestions: examPaper.totalQuestions || 0,
        questionTypes: displayQuestionTypes,
        bloomsTaxonomyOne: Array.isArray(examPaper.bloomsTaxonomyOne)
          ? examPaper.bloomsTaxonomyOne
          : examPaper.bloomsTaxonomyOne
          ? [examPaper.bloomsTaxonomyOne]
          : [],
        bloomsTaxonomyTwo: Array.isArray(examPaper.bloomsTaxonomyTwo)
          ? examPaper.bloomsTaxonomyTwo
          : examPaper.bloomsTaxonomyTwo
          ? [examPaper.bloomsTaxonomyTwo]
          : [],
        isAutogradeEnabled: examPaper.isAutogradeEnabled ?? true,
        isAutomarksEnabled: examPaper.isAutomarksEnabled ?? false,
      };

      setForm(formData);
      setOriginalData(formData);

      if (examPaper.questionPaperPublicPdfUrl) {
        const urlParts = examPaper.questionPaperPublicPdfUrl.split("/");
        setUploadedFileName(urlParts[urlParts.length - 1]);
      }
    }
  }, [data]);

  const hasChanges = () =>
    JSON.stringify(form) !== JSON.stringify(originalData);

  const getChangedFields = () => {
    const changes: any = { id: Number(id) };
    Object.keys(form).forEach((key) => {
      const k = key as keyof CreateExamInput;
      if (JSON.stringify(form[k]) !== JSON.stringify(originalData[k])) {
        changes[k] = form[k];
      }
    });
    return changes;
  };

  const handleChange = (field: keyof CreateExamInput, value: any) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const changedFields = getChangedFields();
      await updateExamPaper({ variables: { input: changedFields } });

      setSnackbarMessage("Exam paper updated successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (err: any) {
      console.error("Failed to update exam paper", err);

      setSnackbarMessage(err.message || "Failed to update exam paper");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  if (loading)
    return (
      <Grid container spacing={2} sx={{ p: 2 }}>
        <Grid item xs={12}>
          <Paper elevation={3} style={{ padding: 16 }}>
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight={400}
            >
              <CircularProgress />
            </Box>
          </Paper>
        </Grid>
      </Grid>
    );

  if (error)
    return (
      <Grid container spacing={2} sx={{ p: 2 }}>
        <Grid item xs={12}>
          <Paper elevation={3} style={{ padding: 16 }}>
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              minHeight={400}
              justifyContent="center"
            >
              <Typography color="error" variant="h6" gutterBottom>
                Error loading exam paper
              </Typography>
              <Typography color="textSecondary">{error.message}</Typography>
              <Button
                variant="outlined"
                onClick={() => history.goBack()}
                style={{ marginTop: 16 }}
              >
                Go Back
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    );

  if (!data?.getExamPapers?.examPapers?.[0])
    return (
      <Grid container spacing={2} sx={{ p: 2 }}>
        <Grid item xs={12}>
          <Paper elevation={3} style={{ padding: 16 }}>
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              minHeight={400}
              justifyContent="center"
            >
              <Typography variant="h6" gutterBottom>
                Exam paper not found
              </Typography>
              <Button
                variant="outlined"
                onClick={() => history.goBack()}
                style={{ marginTop: 16 }}
              >
                Go Back
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    );

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  return (
    <Grid container spacing={2} sx={{ p: 2 }}>
      <Grid item xs={12}>
        <Paper elevation={3} style={{ padding: 16 }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={2}
          >
            <Typography variant="h6">Update Exam Paper #{id}</Typography>
          </Box>
          <Grid container spacing={2} component="form" onSubmit={handleSave}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Title"
                value={form.title}
                onChange={(e) => handleChange("title", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <UploadBox
                setUserAnswerAsPdf={(url: string) =>
                  handleChange("questionPaperPublicPdfUrl", url)
                }
                filename={uploadedFileName}
                setFilename={setUploadedFileName}
                usecase="test_paper_upload"
                acceptedFormats={["pdf"]}
                presignedUrlQuery={GET_PRESIGNED_URL}
                client={autoGradeClient}
                placeholderText="Click to choose question paper"
              />
              {form.questionPaperPublicPdfUrl && (
                <TextField
                  fullWidth
                  margin="dense"
                  label="Question Paper PDF URL"
                  value={form.questionPaperPublicPdfUrl}
                  onChange={(e) =>
                    handleChange("questionPaperPublicPdfUrl", e.target.value)
                  }
                />
              )}
            </Grid>
            <Grid item xs={12} md={4} marginTop={5}>
              <TextField
                fullWidth
                label="Scheduled Date"
                type="datetime-local"
                InputLabelProps={{ shrink: true }}
                value={form.scheduledDate}
                onChange={(e) => handleChange("scheduledDate", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4} marginTop={5}>
              <TextField
                fullWidth
                label="Total Sections"
                type="number"
                value={form.totalSections}
                onChange={(e) =>
                  handleChange("totalSections", Number(e.target.value))
                }
                inputProps={{ min: 1 }}
                disabled
              />
            </Grid>
            <Grid item xs={12} md={4} marginTop={5}>
              <TextField
                fullWidth
                label="Total Marks"
                type="number"
                value={form.totalMarks}
                onChange={(e) =>
                  handleChange("totalMarks", Number(e.target.value))
                }
                inputProps={{ min: 1 }}
                disabled
              />
            </Grid>
            <Grid item xs={12} md={4} marginTop={5}>
              <TextField
                fullWidth
                label="Total Questions"
                type="number"
                value={form.totalQuestions}
                onChange={(e) =>
                  handleChange("totalQuestions", Number(e.target.value))
                }
                inputProps={{ min: 1 }}
                disabled
              />
            </Grid>
            <Grid item xs={12} md={8} marginTop={5}>
              <FormControl fullWidth>
                <InputLabel>Question Types</InputLabel>
                <Select
                  multiple
                  value={form.questionTypes}
                  onChange={(e) =>
                    handleChange("questionTypes", e.target.value)
                  }
                  input={<OutlinedInput label="Question Types" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={value} />
                      ))}
                    </Box>
                  )}
                  disabled
                >
                  {QUESTION_TYPE_OPTIONS.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* BLOOM'S TAXONOMY MULTISELECT */}
            <Grid item xs={12} md={6} marginTop={5}>
              <FormControl fullWidth>
                <InputLabel>Bloom's Taxonomy One</InputLabel>
                <Select
                  multiple
                  value={form.bloomsTaxonomyOne}
                  onChange={(e) =>
                    handleChange("bloomsTaxonomyOne", e.target.value)
                  }
                  input={<OutlinedInput label="Bloom's Taxonomy One" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={value} />
                      ))}
                    </Box>
                  )}
                >
                  {BLOOMS_TAXONOMY1.map((b) => (
                    <MenuItem key={b} value={b}>
                      {b}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6} marginTop={5}>
              <FormControl fullWidth>
                <InputLabel>Bloom's Taxonomy Two</InputLabel>
                <Select
                  multiple
                  value={form.bloomsTaxonomyTwo}
                  onChange={(e) =>
                    handleChange("bloomsTaxonomyTwo", e.target.value)
                  }
                  input={<OutlinedInput label="Bloom's Taxonomy Two" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={value} />
                      ))}
                    </Box>
                  )}
                >
                  {BLOOMS_TAXONOMY2.map((b) => (
                    <MenuItem key={b} value={b}>
                      {b}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.isAutogradeEnabled}
                    onChange={(e) =>
                      handleChange("isAutogradeEnabled", e.target.checked)
                    }
                  />
                }
                label="Enable Auto-grade"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.isAutomarksEnabled}
                    onChange={(e) =>
                      handleChange("isAutomarksEnabled", e.target.checked)
                    }
                  />
                }
                label="Enable Auto-marks"
              />
            </Grid>

            <Grid item xs={12}>
              <Box display="flex" justifyContent="flex-end">
                <Button
                  variant="outlined"
                  onClick={() => history.goBack()}
                  style={{ marginRight: 8 }}
                >
                  Close
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={!hasChanges() || updating}
                >
                  {updating ? "Updating..." : "Update"}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Grid>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Grid>
  );
}

export default UpdateExamPaperPage;
